import { getToken } from './auth';
import { message } from 'antd';

/**
 * WebSocket连接状态枚举
 */
export const WebSocketStatus = {
  CONNECTING: 0,  // 连接中
  OPEN: 1,        // 已连接
  CLOSING: 2,     // 关闭中
  CLOSED: 3       // 已关闭
};

/**
 * 消息类型枚举 - 与后端保持一致
 */
export const MessageType = {
  CONNECT: 'CONNECT',                    // 连接成功
  HEARTBEAT: 'HEARTBEAT',               // 心跳检测
  SYSTEM_NOTIFICATION: 'SYSTEM_NOTIFICATION', // 系统通知消息
};

/**
 * 生成消息UID
 * @returns {string} 消息UID
 */
export const generateMessageUid = () => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 客服聊天WebSocket工具类
 */
class CustomerServiceWebSocket {
  constructor() {
    this.ws = null;
    this.url = null;
    this.userType = null;
    this.userId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = Infinity; // 取消重连次数限制
    this.reconnectInterval = 3000; // 3秒
    this.heartbeatInterval = 30000; // 30秒
    this.heartbeatTimer = null;
    this.reconnectTimer = null;
    this.messageHandlers = new Map();
    this.connectionHandlers = new Map();
    this.isManualClose = false;
    this.isManualReconnect = false; // 标记是否为手动重连
    this.sessionId = null;
  }

  /**
   * 连接WebSocket
   * @param {string} userType 用户类型 (customer/service)
   * @param {string} userId 用户ID
   * @param {string} sessionId 会话ID (可选)
   * @param {string} baseUrl WebSocket基础URL
   * @param {boolean} isManualReconnect 是否为手动重连
   */
  connect(userType, userId, sessionId = null, baseUrl = null, isManualReconnect = false) {
    if (this.ws && this.ws.readyState === WebSocketStatus.OPEN) {
      console.log('WebSocket已连接');
      return;
    }

    this.userType = userType;
    this.userId = userId;
    this.sessionId = sessionId;
    this.isManualReconnect = isManualReconnect;
    
    // 获取token
    const token = getToken();
    if (!token) {
      message.error('未登录，无法建立WebSocket连接');
      return;
    }

    // 构建WebSocket URL，通过URL参数传递token
    const wsBaseUrl = baseUrl || this.getWebSocketBaseUrl();
    this.url = `${wsBaseUrl}/websocket/cs/${userType}/${userId}?token=${encodeURIComponent(token)}`;

    try {
      // 创建WebSocket连接
      this.ws = new WebSocket(this.url);
      
      // 设置连接事件监听
      this.setupEventListeners();

    } catch (error) {
      console.error('WebSocket连接失败:', error);
      message.error('WebSocket连接失败');
      this.handleReconnect();
    }
  }

  /**
   * 获取WebSocket基础URL
   */
  getWebSocketBaseUrl() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.REACT_APP_WS_HOST || window.location.host;
    console.log("getWebSocketBaseUrl", protocol, host);
    return `${protocol}//${host}`;
  }

  /**
   * 设置事件监听器
   */
  setupEventListeners() {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('WebSocket连接已建立', {
        url: this.url,
        userType: this.userType,
        userId: this.userId,
        readyState: this.ws.readyState
      });
      this.reconnectAttempts = 0;
      this.isManualClose = false;
      this.startHeartbeat();
      this.notifyConnectionHandlers('open');
      // 连接建立后通知认证成功
      this.notifyConnectionHandlers('authenticated');
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (error) {
        console.error('解析WebSocket消息失败:', error);
      }
    };

    this.ws.onclose = (event) => {
      console.log('WebSocket连接已关闭:', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        isManualClose: this.isManualClose,
        isManualReconnect: this.isManualReconnect,
        reconnectAttempts: this.reconnectAttempts
      });
      this.stopHeartbeat();
      this.notifyConnectionHandlers('close', event);
      
      // 手动重连优先于自动重连
      if (this.isManualReconnect) {
        console.log('手动重连进行中，跳过自动重连');
        return;
      }
      
      if (!this.isManualClose) {
        console.log('尝试自动重连...');
        this.handleReconnect();
      } else {
        console.log('手动关闭连接，不进行重连');
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket连接错误:', error);
      this.notifyConnectionHandlers('error', error);
    };
  }

  /**
   * 发送认证消息（已废弃，现在通过请求头认证）
   * @param {string} token 认证token
   */
  sendAuthMessage(token) {
    console.warn('sendAuthMessage已废弃，现在通过请求头进行认证');
    // 保留方法以兼容旧代码，但不执行任何操作
  }

  /**
   * 处理接收到的消息
   * @param {Object} data 消息数据
   */
  handleMessage(data) {
    console.log("收到消息", data);
    const { type } = data;
 
    // 处理心跳响应
    if (type === MessageType.HEARTBEAT) {
      console.log('收到心跳响应');
      return;
    }

    // 处理认证响应（现在通过请求头认证，这里保留兼容性）
    if (type === 'AUTH_RESPONSE') {
      if (data.success) {
        console.log('WebSocket认证成功');
        this.notifyConnectionHandlers('authenticated');
      } else {
        console.error('WebSocket认证失败:', data.message);
        message.error('WebSocket认证失败');
        this.close();
      }
      return;
    }

    // 通知消息处理器
    if (this.messageHandlers.has(type)) {
      this.messageHandlers.get(type).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('处理消息时出错:', error);
        }
      });
    }

    // 处理特定消息类型
    switch (type) {
      case MessageType.USER_MESSAGE:
      case MessageType.AI_RESPONSE:
      case MessageType.AGENT_MESSAGE:
        this.notifyMessageHandlers('message', data);
        break;
      case MessageType.SYSTEM_NOTIFICATION:
        this.notifyMessageHandlers('system', data);
        break;
     
      default:
        console.log('未知消息类型:', type, data);
    }
  }

  /**
   * 发送消息
   * @param {Object} message 消息对象
   */
  send(message) {
    if (!this.ws || this.ws.readyState !== WebSocketStatus.OPEN) {
      console.error('WebSocket未连接，无法发送消息');
      return false;
    }

    try {
      const messageStr = JSON.stringify(message);
      console.log("发送文本消息str", messageStr);
      this.ws.send(messageStr);
      return true;
    } catch (error) {
      console.error('发送消息失败:', error);
      return false;
    }
  }

  /**
   * 发送文本消息
   * @param {string} content 消息内容
   * @param {string} sessionId 会话ID
   * @param {Object} options 其他选项
   */
  sendTextMessage(content, sessionId = null, options = {}) {
    const now = Date.now();
    console.log("发送文本消息", options);
    const message = {
      type: MessageType.AGENT_MESSAGE,
      content: content,
      sessionId: sessionId || this.sessionId,
      senderId: options.senderId,
      senderType: options.senderType || 'AGENT',
      receiverId: options.receiverId,
      uid: options.uid,
      createTime: options.createTime,
      data: {
        messageType: 'TEXT',
        ...options.data
      },
      timestamp: now,
      isRead: false,
      status: 'SENDING',
      createTime: now
    };
    console.log("发送文本消息ddd", message);
    return this.send(message);
  }

  /**
   * 发送图片消息
   * @param {string} imageUrl 图片URL
   * @param {string} sessionId 会话ID
   * @param {Object} options 其他选项
   */
  sendImageMessage(imageUrl, sessionId = null, options = {}) {
    const now = Date.now();
    const message = {
      type: MessageType.AGENT_MESSAGE,
      content: imageUrl,
      sessionId: sessionId || this.sessionId,
      senderId: options.senderId,
      senderType: options.senderType || 'AGENT',
      receiverId: options.receiverId,
      data: {
        messageType: 'IMAGE',
        imageUrl: imageUrl,
        ...options.data
      },
      timestamp: now,
      id: generateMessageUid(),
      messageId: now,
      isRead: false,
      status: 'SENDING',
      createTime: now
    };
    return this.send(message);
  }

  /**
   * 发送文件消息
   * @param {string} fileUrl 文件URL
   * @param {string} fileName 文件名
   * @param {string} sessionId 会话ID
   * @param {Object} options 其他选项
   */
  sendFileMessage(fileUrl, fileName, sessionId = null, options = {}) {
    const now = Date.now();
    const message = {
      type: MessageType.AGENT_MESSAGE,
      content: fileName,
      sessionId: sessionId || this.sessionId,
      senderId: options.senderId,
      senderType: options.senderType || 'AGENT',
      receiverId: options.receiverId,
      data: {
        messageType: 'FILE',
        fileUrl: fileUrl,
        fileName: fileName,
        ...options.data
      },
      timestamp: now,
      id: generateMessageUid(),
      messageId: now,
      isRead: false,
      status: 'SENDING',
      createTime: now
    };
    return this.send(message);
  }

  /**
   * 发送正在输入状态
   * @param {boolean} isTyping 是否正在输入
   * @param {string} sessionId 会话ID
   */
  sendTypingStatus(isTyping, sessionId = null) {
    const now = Date.now();
    const message = {
      type: MessageType.TYPING_INDICATOR,
      content: isTyping ? '正在输入...' : '',
      sessionId: sessionId || this.sessionId,
      senderId: this.userId,
      senderType: 'AGENT',
      data: {
        isTyping: isTyping
      },
      timestamp: now,
      id: generateMessageUid(),
      messageId: now,
      isRead: false,
      status: 'SENT',
      createTime: now
    };
    return this.send(message);
  }

  /**
   * 发送已读回执
   * @param {string} messageId 消息ID
   * @param {string} sessionId 会话ID
   */
  sendReadReceipt(messageId, sessionId = null) {
    const now = Date.now();
    const message = {
      type: MessageType.MESSAGE_READ,
      content: '',
      sessionId: sessionId || this.sessionId,
      senderId: this.userId,
      senderType: 'AGENT',
      data: {
        messageId: messageId
      },
      timestamp: now,
      id: generateMessageUid(),
      messageId: now,
      isRead: true,
      status: 'READ',
      createTime: now
    };
    return this.send(message);
  }

  /**
   * 发送转接消息
   * @param {string} targetServiceId 目标客服ID
   * @param {string} reason 转接原因
   * @param {string} sessionId 会话ID
   */
  sendTransferMessage(targetServiceId, reason, sessionId = null) {
    const now = Date.now();
    const message = {
      type: MessageType.TRANSFER_REQUEST,
      content: reason,
      sessionId: sessionId || this.sessionId,
      senderId: this.userId,
      senderType: 'AGENT',
      receiverId: targetServiceId,
      data: {
        targetServiceId: targetServiceId,
        reason: reason
      },
      timestamp: now,
      id: generateMessageUid(),
      messageId: now,
      isRead: false,
      status: 'SENDING',
      createTime: now
    };
    return this.send(message);
  }

  /**
   * 发送心跳消息
   */
  sendHeartbeat() {
    const now = Date.now();
    const message = {
      type: MessageType.HEARTBEAT,
      content: '',
      sessionId: this.sessionId,
      senderId: this.userId,
      senderType: this.userType === 'service' ? 'AGENT' : 'USER',
      data: {
        timestamp: now
      },
      timestamp: now,
      id: generateMessageUid(),
      messageId: now,
      isRead: false,
      status: 'SENT',
      createTime: now
    };
    return this.send(message);
  }

  /**
   * 开始心跳
   */
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.sendHeartbeat();
    }, this.heartbeatInterval);
  }

  /**
   * 停止心跳
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 处理重连
   */
  handleReconnect() {
    // 取消重连次数限制
    this.reconnectAttempts++;
    console.log(`WebSocket重连尝试 ${this.reconnectAttempts} (无限制)`);

    this.reconnectTimer = setTimeout(() => {
      if (this.userType && this.userId) {
        console.log("重连",this.getWebSocketBaseUrl());
        this.connect(this.userType, this.userId, this.sessionId,this.getWebSocketBaseUrl());
      }
    }, this.reconnectInterval);
  }

  /**
   * 手动重连
   */
  manualReconnect() {
    console.log('开始手动重连...');
    
    // 清除自动重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // 重置重连计数
    this.reconnectAttempts = 0;
    
    // 关闭现有连接
    this.close();
    
    // 延迟后手动重连
    setTimeout(() => {
      if (this.userType && this.userId) {
        this.connect(this.userType, this.userId, this.sessionId, null, true);
      }
    }, 1000);
  }

  /**
   * 关闭连接
   */
  close() {
    this.isManualClose = true;
    this.stopHeartbeat();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, '正常关闭');
      this.ws = null;
    }
  }

  /**
   * 获取连接状态
   */
  getStatus() {
    return this.ws ? this.ws.readyState : WebSocketStatus.CLOSED;
  }

  /**
   * 检查是否已连接
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocketStatus.OPEN;
  }

  /**
   * 添加消息处理器
   * @param {string} type 消息类型
   * @param {Function} handler 处理函数
   */
  onMessage(type, handler) {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type).push(handler);
  }

  /**
   * 移除消息处理器
   * @param {string} type 消息类型
   * @param {Function} handler 处理函数
   */
  offMessage(type, handler) {
    if (this.messageHandlers.has(type)) {
      const handlers = this.messageHandlers.get(type);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * 添加连接状态处理器
   * @param {string} event 事件类型
   * @param {Function} handler 处理函数
   */
  onConnection(event, handler) {
    if (!this.connectionHandlers.has(event)) {
      this.connectionHandlers.set(event, []);
    }
    this.connectionHandlers.get(event).push(handler);
  }

  /**
   * 移除连接状态处理器
   * @param {string} event 事件类型
   * @param {Function} handler 处理函数
   */
  offConnection(event, handler) {
    if (this.connectionHandlers.has(event)) {
      const handlers = this.connectionHandlers.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * 通知消息处理器
   * @param {string} type 消息类型
   * @param {Object} data 消息数据
   */
  notifyMessageHandlers(type, data) {
    if (this.messageHandlers.has(type)) {
      this.messageHandlers.get(type).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('处理消息时出错:', error);
        }
      });
    }
  }

  /**
   * 通知连接状态处理器
   * @param {string} event 事件类型
   * @param {Object} data 事件数据
   */
  notifyConnectionHandlers(event, data) {
    if (this.connectionHandlers.has(event)) {
      this.connectionHandlers.get(event).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error('处理连接事件时出错:', error);
        }
      });
    }
  }

  /**
   * 销毁实例
   */
  destroy() {
    this.close();
    this.messageHandlers.clear();
    this.connectionHandlers.clear();
  }
}

// 创建单例实例
const customerServiceWebSocket = new CustomerServiceWebSocket();

export default customerServiceWebSocket;
export { CustomerServiceWebSocket }; 